// Historial de liquidaciones (ver informe "Sprint Final — Cierre semanal",
// Parte 3): lista SOLO las semanas ya CERRADAS (GET /v1/liquidaciones/periodos).
// Clickear una fila abre esa semana en el Dashboard con ?period=<period_key>
// — el mismo Dashboard, en modo solo lectura (ver LiquidacionesDashboard.tsx).
// No hay una pantalla de detalle propia acá: el historial es solo un índice.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import { useAuth } from "../context/AuthContext";
import { authFetch } from "../lib/authFetch";

type PeriodRow = {
  id: number;
  period_key: string;
  starts_on: string;
  ends_on: string;
  status: "OPEN" | "CLOSED";
  closed_at: string | null;
  created_at: string;
};

type PeriodosResponse = { ok: boolean; current: PeriodRow; closed: PeriodRow[] };

const pageWrapStyle: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "16px" };
const headerRowStyle: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "12px" };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: "26px", fontWeight: 800, color: "var(--text-primary)", letterSpacing: "-0.02em" };
const subtitleStyle: React.CSSProperties = { margin: "6px 0 0 0", color: "var(--text-secondary)", fontSize: "14px" };
const tableWrapStyle: React.CSSProperties = { overflowX: "auto", border: "1px solid var(--border)", borderRadius: "18px" };
const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "10px", color: "var(--text-secondary)", fontSize: "12px", fontWeight: 700, borderBottom: "1px solid var(--border)", whiteSpace: "nowrap", background: "rgba(255,255,255,0.02)" };
const tdStyle: React.CSSProperties = { padding: "10px", color: "var(--text-primary)", fontSize: "13px", borderBottom: "1px solid var(--border)", whiteSpace: "nowrap" };
const trClickableStyle: React.CSSProperties = { cursor: "pointer" };
const emptyStyle: React.CSSProperties = { padding: "32px 12px", color: "var(--text-secondary)", fontSize: "14px", textAlign: "center" };
const badgeStyle = (color: string): React.CSSProperties => ({ display: "inline-block", padding: "2px 9px", borderRadius: "999px", fontSize: "11px", fontWeight: 700, border: `1px solid ${color}`, color });

// Tolerante a "YYYY-MM-DD" — starts_on/ends_on ya vienen como fecha ::text.
function formatFecha(raw: string | null): string {
  if (!raw) return "—";
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
  if (iso) return `${iso[3]}/${iso[2]}/${iso[1]}`;
  return raw;
}

export default function LiquidacionesHistorial() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<PeriodosResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    authFetch("/v1/liquidaciones/periodos", { token })
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok || payload?.ok === false) throw new Error(payload?.error || "Error al cargar el historial de liquidaciones");
        if (!cancelled) setData(payload);
      })
      .catch((e: any) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div style={pageWrapStyle}>
      <div style={headerRowStyle}>
        <div>
          <h1 style={titleStyle}>Historial de liquidaciones</h1>
          <p style={subtitleStyle}>Semanas ya cerradas — se abren de solo lectura, con los mismos datos que quedaron al cierre.</p>
        </div>
        <Button variant="secondary" onClick={() => navigate("/admin/liquidaciones/dashboard")}>
          Volver a la semana actual
        </Button>
      </div>

      {loading && <Card>Cargando…</Card>}
      {!loading && error && (
        <Card>
          <span style={{ color: "var(--danger, #d1242f)" }}>{error}</span>
        </Card>
      )}

      {!loading && !error && data && (
        <Card padding="0">
          <div style={{ padding: "16px" }}>
            {data.closed.length === 0 ? (
              <div style={emptyStyle}>Todavía no hay semanas cerradas — la semana actual sigue abierta.</div>
            ) : (
              <div style={tableWrapStyle}>
                <table style={tableStyle}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Semana</th>
                      <th style={thStyle}>Estado</th>
                      <th style={thStyle}>Cerrada el</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.closed.map((p) => (
                      <tr
                        key={p.id}
                        style={trClickableStyle}
                        onClick={() => navigate(`/admin/liquidaciones/dashboard?period=${encodeURIComponent(p.period_key)}`)}
                      >
                        <td style={tdStyle}>{formatFecha(p.starts_on)} → {formatFecha(p.ends_on)}</td>
                        <td style={tdStyle}>
                          <span style={badgeStyle("#c99a1f")}>CERRADA</span>
                        </td>
                        <td style={tdStyle}>{p.closed_at ? formatFecha(p.closed_at) : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
